import { useBlockStore } from "@/frontend/store/BlockStore";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { I18n } from "@/i18n/I18n";
import { SourceTypes } from "@/types/containerTypes";
import { useEffect, useState, useRef } from "react";
import { Modal, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Container } from "@/backend/models/containers/container";
import { CollapseButton } from "../buttons/CollapseButton";
import { DropdownMenu } from "../dropdown/DropdownMenu";
import { useParseProcedureReferences } from "@/frontend/hooks/parameters";
import { isEmbedApp } from "@/frontend/embed/embedApp";

export function BasicContainer(props: {
  container: Container;
  wbs?: string;
  showSection: boolean;
  setShowSection: (b: boolean) => void;
}) {
  const { container, wbs, showSection, setShowSection } = props;
  const duplicateContainer = useTreeStore((state) => state.duplicateContainer);
  const saveContainerAsBlock = useBlockStore((state) => state.saveContainerAsBlock);
  const deleteContainer = useTreeStore((state) => state.deleteContainer);
  const childContainers = useTreeStore((state) =>  state.childContainers);
  const updateContainer = useTreeStore((state) => state.updateContainer);
  const orderContainers = useTreeStore((state) => state.orderContainers);
  const parseProcedureReferences = useParseProcedureReferences(container);
  const [title, setTitle] = useState(container.title);
  const [error, setError] = useState('');
  const observer = useTreeStore((state) => state.observer);
  const listOfItems = childContainers(container.parentId, container.isScaffold).map(tree => tree.container);

  useEffect(() => {
    setTitle(container.title);
  }, [container.title]);

  const onChangeTitle = (value: string) => {
    setTitle(value);
    setError(parseProcedureReferences(value) || '');
  };

  const commitTitle = () => {
    if (!title.trim()) {
      setTitle(container.title); // revert: never leave/commit a blank title
      return;
    }
    if (title === container.title) return;
    void updateContainer({ container: { ...container, title } });
  };

  const dropDownChildren = [];

  // The template editor is only served by the embed app.
  if (container.sourceType === SourceTypes.Scaffold && isEmbedApp()) {
    dropDownChildren.push(
      <Link className='d-block btn-link dropdown-item' to={`/procedures/scaffold/${container.rootId}#${container.sourceId}`} key={`${container.id}Source`}>
        {I18n.get('goToSource', { containerType: I18n.get(container.containerType) })}
      </Link>
    );
  }

  if (container.isStep()) {
    dropDownChildren.push(
      <Link className='d-block dropdown-item' to={`/procedures/wizard/${container.rootId}/steps/${container.id}`} key={`${container.id}Wizard`}>
        {I18n.get('viewInWizard')}
      </Link>
    );
  }

  dropDownChildren.push(
    <button
      type="button"
      className="dropdown-item"
      onClick={() => setShowDescriptionModal(true)}
      key={`${container.id}EditDescription`}
    >
      {I18n.get('editDescription')}
    </button>
  );

  useEffect(() => {
    const observedItem = document.getElementById(`${container.id}`);
    if (observer && observedItem) {
      observer.observe(observedItem as HTMLElement);
    }
    return () => {
      if (observer && observedItem) {
        observer.unobserve(observedItem as HTMLElement);
      }
    };
  });

  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const containerRef = useRef(null);
  const [description, setDescription] = useState(container.description);

  // const handleOllamaComplete = (response: string) => {
  //   setDescription(response);
  //   updateContainer({ container: { ...container, description: response } });
  // };

  return (
    <div className="d-flex flex-column">
      <div className="d-flex justify-content-between w-100">
        <div>
          <CollapseButton
            showElement={showSection}
            setShowElement={setShowSection}
            title={I18n.get(container.containerType)}
          />
          <small className={`text-muted text-uppercase`}>
            <label aria-label={I18n.get(container.containerType)} htmlFor={container.id}>
              {I18n.get(container.containerType)}
            </label>
          </small>
          <small className='ps-1 fw-bold'>{wbs}</small>
        </div>
        <DropdownMenu
          onClickDupe={() => void duplicateContainer(container.id)}
          saveAsBlock={(blockTitle: string) => void saveContainerAsBlock(container, blockTitle)}
          instance={container.containerType}
          orderItems={orderContainers}
          onClickDelete={() => deleteContainer(container)}
          listOfItems={listOfItems}
          item={container}
        >
          {dropDownChildren}
        </DropdownMenu>
      </div>
      <Form.Label className="col-form-label col-form-label-sm">{I18n.get('Title')}</Form.Label>
      <Form.Control 
        type="text"
        size='sm' 
        value={title} 
        onChange={(e) => onChangeTitle(e.target.value)} 
        isInvalid={!!error}
        onBlur={commitTitle}
      />
      {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
      <Modal show={showDescriptionModal} onHide={() => setShowDescriptionModal(false)}>
        <Modal.Header closeButton>
          <div className="d-flex flex-column  gap-2 w-100">
            <Modal.Title>{I18n.get('editTypeDescription', { type: I18n.get(container.containerType.charAt(0).toUpperCase() + container.containerType.slice(1)) })}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column gap-2" ref={containerRef}>
            <textarea
              className="form-control"
              id={`description-${container.id}`}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => void updateContainer({ container: { ...container, description } })}
              value={description}
              placeholder={I18n.get('description')}
              style={{ minHeight: '12em', resize: 'vertical' }}
              aria-label={I18n.get('description')}
            />
            {/* <div className="d-flex justify-content-end">
              <OllamaChatGenerator
                onComplete={handleOllamaComplete}
                buttonText={I18n.get('ollamaGenerateChat')}
                buttonClassName="btn-sm"
                containerRef={containerRef}
              />
            </div> */}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
